<template>
  <!-- prettier-ignore -->
  <div class="Expander">
    <div
      class="trigger"
      :class="$props.open ? 'active' : 'beforeBorder'"
      @click="toggle"
    >
      <span
        class="title"
        v-html="$props.titleLabel"
      />
      <the-icon
        art="regular"
        class="trigger-icon"
        :name="triggerIcon"
      />
    </div>
    <transition :name="$props.animation">
      <div
        v-show="$props.open"
        class="body"
      >
        <slot />
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed } from 'vue'

// INIT
const emit = defineEmits(['opened', 'closed'])
const props = defineProps({
  animation: {
    type: String,
    default: 'topToBottom',
    // validator: prop => ['leftToRight', 'bounceIn', 'bottomToTop'].includes(prop)
  },
  titleLabel: {
    type: String,
    default: 'title',
  },
  open: {
    type: Boolean,
    default: false
  }
})

// COMPUTED
const triggerIcon = computed(() => {
  return props.open ? 'minus' : 'plus'
})

// METHODS
function toggle() {
  props.open ? emit('closed') : emit('opened')
}
</script>

<style>
.beforeBorder {
  position: relative;

  &:before {
    transition:
      opacity 0.1s linear,
      transform 0.5s ease-in-out;
    position: absolute;
    border-bottom: 1px solid var(--c-primary-color-3);
    content: '';
    width: 100%;
    left: 0;
    bottom: -1px;
  }

  &:not(:hover)::before {
    transform: scaleX(0);
    opacity: 0;
  }
}

.Expander {
  overflow: hidden;
  position: sticky;

  .trigger {
    position: relative;
    align-items: center;
    background-color: white;
    border-bottom: 1px solid var(--colors-neutral-a-100);
    cursor: pointer;
    display: flex;
    padding: var(--fixed-spacing-fix-03) 0;
    z-index: 1;

    &:hover {
      color: var(--c-primary-color-3);
    }

    &.active {
      border: none;
    }

    .title {
      flex: 1;
      font-size: var(--font-size-bold-sm);
      font-weight: var(--font-weight-bold);
      letter-spacing: var(--letter-spacing-bold-sm);
      line-height: var(--line-height-bold-sm);
    }

    .trigger-icon {
      width: 18px;
      height: 20px;
      color: var(--primary);
    }
  }

  .body {
    border-bottom: 1px solid var(--colors-neutral-a-100);
    padding-bottom: var(--fixed-spacing-fix-03);
  }
}

@media screen and (max-width: 299px) {
  .Expander {
    .trigger {
      .title {
        font-size: var(--pw-font-size-small);
      }

      .trigger-icon-wrapper {
        width: 30px;
      }
    }
  }
}

.rightToLeft-enter-active {
  animation: rightToLeft 0.5s;
}

.rightToLeft-leave-active {
  animation: rightToLeft 0.5s reverse;
}

@keyframes rightToLeft {
  0% {
    transform: translateX(100vw);
  }
  50% {
    transform: translateX(-2em);
  }
  100% {
    transform: translateX(0);
  }
}

.leftToRight-enter-active {
  animation: leftToRight 0.5s;
}

.leftToRight-leave-active {
  animation: leftToRight 0.5s reverse;
}

@keyframes leftToRight {
  0% {
    transform: translateX(-100vw);
  }
  50% {
    transform: translateX(2em);
  }
  100% {
    transform: translateX(0);
  }
}

.topToBottom-enter-active {
  animation: topToBottom 0.5s forwards;
}

.topToBottom-leave-active {
  animation: topToBottom 0.5s reverse;
}

@keyframes topToBottom {
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(0);
  }
}
</style>
